import { useEffect, useMemo, useState } from "react"
import { Col, Row, Steps, Typography } from "antd"
import { isEmpty, isNumber } from "lodash"
import "bootstrap/dist/css/bootstrap.min.css"
import { camelToTitleCase, isNumericOrNumericString } from "../Tools/String"
import { getLoanAmount } from "../Tools/Loan"
import { getPropertyAddress } from "./propertyAddress"
import * as com from "../Common"
import { getLoansV2 } from "./api/loansV2Client"
import "../Application/Overview.css"

const { Text } = Typography

const money = (value) => (isNumericOrNumericString(value) ? `$ ${com.commaize(value)}` : "$--")

const normalizeLoanStage = (stage) => {
    const raw = `${stage || ""}`.trim()
    if (!raw) return "Application"
    if (com.CurrentStage[raw] != null) return raw
    const pascal = raw
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("")
    if (com.CurrentStage[pascal] != null) return pascal
    return "Application"
}

const SummaryItem = ({ label, value }) => (
    <div>
        <div style={{ color: "#6E6E70", fontSize: "14px", fontWeight: 500, marginBottom: "6px" }}>
            {label}
        </div>
        <div style={{ color: "#333333", fontSize: "18px", fontWeight: 500 }}>
            {value}
        </div>
    </div>
)

const OverviewV2 = ({ application }) => {
    const [loanOfficerName, setLoanOfficerName] = useState(
        () => localStorage.getItem("LOAN_OFFICER_NAME") || ""
    )

    const property = application?.property || {}
    const selection = application?.selection || {}
    const selectedLoan = selection?.selectedloan || {}
    const purpose = property.purpose
    const mortgageApplied = application?.product?.mortgageApplied || application?.product?.mortgageapplied || ""
    const hardMoney = application?.hardMoneyLoan || application?.hardmoneyloan || {}

    const priceValue = purpose === "purchase" && property.hascontract ? property.salesprice : property.appraisal
    const paymentValue = purpose === "purchase"
        ? selection.downpayment
        : purpose === "refinance"
            ? selection.paydown
            : selection.cashout

    const purposeTitle = useMemo(() => {
        if (!purpose) return "Loan Application"
        if (purpose === "cashoutrefinance") return "Cashout"
        if (`${mortgageApplied}`.toLowerCase() === "hardmoneyloan") return "Hard Money Loan"
        return `Home ${purpose.charAt(0).toUpperCase()}${purpose.slice(1)}`
    }, [purpose, mortgageApplied])

    const address = useMemo(() => getPropertyAddress(property), [property])

    const currentStage = normalizeLoanStage(application?.currentStage)
    const stageNames = ["Application", "Processing", "Closing"]
    const stageIndex = Math.max(0, stageNames.findIndex((stage) => stage === currentStage))

    const updatedAt = application?.updatedAt ? new Date(application.updatedAt) : null
    const statusLabel = application?.currentStatus
        ? (com.CurrentStatusUtils?.getStatusName?.(application.currentStatus)
            || camelToTitleCase(application.currentStatus))
        : "In progress"

    const baseRate = selectedLoan.base_rate
    const apr = selectedLoan.APR ?? selectedLoan.apr
    const monthlyPayment = selectedLoan.monthly_payment
    const closingCost = selectedLoan.closing_cost
    const points = selectedLoan.points

    const formatRateAndAPR = () => {
        const isValidRate = isNumber(baseRate)
        const isValidAPR = isNumber(apr)
        if (!isValidRate && !isValidAPR) return "---"
        if (!isValidRate) return `---/${apr}%`
        if (!isValidAPR) return `${baseRate}%/---`
        return `${baseRate}%/${apr}%`
    }

    const loanAmountDisplay = getLoanAmount(
        purpose,
        mortgageApplied,
        property.salesprice,
        selection.downpayment,
        hardMoney.initialloanamount,
        hardMoney.refinanceloanamount,
        selection.loansize,
        property.hascontract,
        property.appraisal,
        hardMoney.loantype,
        hardMoney.rehaborconstructionloanamount,
    )

    const priceLabel = purpose === "purchase"
        ? "Purchase price"
        : "Estimated home value"
    const paymentLabel = purpose === "purchase"
        ? "Down payment"
        : purpose === "refinance"
            ? "Principal paydown"
            : "Cash out"

    useEffect(() => {
        let cancelled = false
        const loadLoanOfficer = async () => {
            try {
                const body = await getLoansV2()
                const loans = Array.isArray(body.loans) ? body.loans : []
                const currentId = application?.loanId || body.currentLoanId
                const loan = loans.find((item) => item.loanId === currentId) || loans[0]
                const name = loan?.loanOfficerName || ""
                if (!cancelled && name) {
                    setLoanOfficerName(name)
                    localStorage.setItem("LOAN_OFFICER_NAME", name)
                }
            } catch (err) {
                console.error("OverviewV2: load loan officer failed", err)
            }
        }
        loadLoanOfficer()
        return () => {
            cancelled = true
        }
    }, [application?.loanId])

    return (
        <div style={{ margin: "0 4px" }} className="text-left text-wrap newInterviewPaneEmployment">
            <Row>
                <Col>
                    <div className="page-header">
                        <span className="page-header-primary">Overview</span>
                    </div>
                </Col>
            </Row>

            <Row gutter={{ md: 64 }} className="mt-4">
                <Col md={10} span={24} style={{ marginBottom: 30 }}>
                    <div className="home-card">
                        <div style={{ width: 30, height: 30 }}>
                            <img src={purpose === "purchase" ? "/house.svg" : "/money.svg"} alt="loan" />
                        </div>
                        <div style={{ marginLeft: 12 }}>
                            <Text style={{ display: "block", fontSize: 16, fontWeight: 600, color: "#081265", marginBottom: 10 }}>
                                {purposeTitle.toUpperCase()}
                            </Text>
                            <Text
                                style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: 18,
                                    color: "#333333",
                                    marginBottom: 10,
                                }}
                            >
                                {address}
                            </Text>
                            <Text style={{ display: "block", fontSize: 16, color: "#6E6E70" }}>
                                Last updated on{" "}
                                {updatedAt && !Number.isNaN(updatedAt.getTime())
                                    ? updatedAt.toLocaleDateString()
                                    : "--"}
                            </Text>
                        </div>
                    </div>
                </Col>
                <Col md={14} span={24} style={{ marginBottom: 30 }}>
                    <div className="loan-tracker">
                        <Text style={{ display: "block", fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
                            Loan tracker
                        </Text>
                        <Steps
                            className="overview-steps"
                            responsive={false}
                            current={stageIndex}
                            items={stageNames.map((title) => ({ title }))}
                        />
                        <div className="mt-3" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                            <Text style={{ color: "#62707C", marginRight: 8 }}>Current status</Text>
                            <div
                                style={{
                                    height: 32,
                                    padding: "0 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    border: "1px solid #1630AC",
                                    background: "#FFFFFF",
                                    color: "#1630AC",
                                    borderRadius: 8,
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                {statusLabel}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Text style={{ display: "block", fontSize: 18, fontWeight: 500, color: "#6E6E70", marginBottom: 10 }}>
                Loan summary
            </Text>
            <div className="loan-summary-table">
                <Row gutter={[48, 20]}>
                    <Col md={6} span={12}>
                        <SummaryItem label={priceLabel} value={money(priceValue)} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem label="Loan amount" value={loanAmountDisplay} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem label={paymentLabel} value={money(paymentValue)} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem
                            label="Loan officer"
                            value={isEmpty(loanOfficerName) ? "N/A" : loanOfficerName}
                        />
                    </Col>
                </Row>

                <div style={{ borderBottom: "1px solid #e5e7eb", margin: "20px 0" }} />

                <Row gutter={[48, 20]}>
                    <Col md={6} span={12}>
                        <SummaryItem label="Rate/APR" value={formatRateAndAPR()} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem label="Mo.payment" value={money(monthlyPayment)} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem label="Closing cost" value={money(closingCost)} />
                    </Col>
                    <Col md={6} span={12}>
                        <SummaryItem
                            label="Point"
                            value={isNumericOrNumericString(points) ? `${points}%` : "---"}
                        />
                    </Col>
                </Row>
            </div>
        </div>
    )
}

export default OverviewV2
